import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  Stack,
  Divider,
} from '@mui/material';
import {
  CompareArrows as CompareIcon,
  BarChart as ChartIcon,
  Speed as SpeedIcon,
  AttachMoney as CostIcon,
  Analytics as CoverageIcon,
} from '@mui/icons-material';
// Removed recharts import - using Material-UI components for visualization instead

interface ComparisonMetric {
  strategy: string;
  sampleSize: number;
  coverageScore: number;
  costEstimate: number;
  processingTime: number;
  impressionDistribution: {
    high: number;
    medium: number;
    low: number;
  };
  pros: string[];
  cons: string[];
}

const COLORS = {
  high: '#4caf50',
  medium: '#ff9800',
  low: '#2196f3',
};

const SamplingComparisonDemo: React.FC = () => {
  const [clientId, setClientId] = useState('157');
  const [totalKeywords, setTotalKeywords] = useState(15000);
  const [apiCostPerQuery, setApiCostPerQuery] = useState(0.002); // $0.002 per query
  const [selectedStrategies, setSelectedStrategies] = useState<string[]>([
    'representative',
    'highVolume',
    'stratified',
  ]);
  const [sampleSizes, setSampleSizes] = useState({
    representative: 200,
    highVolume: 150,
    stratified: 250,
    random: 200,
    custom: 300,
  });
  const [loading, setLoading] = useState(false);
  const [comparisonData, setComparisonData] = useState<ComparisonMetric[]>([]);

  const strategies = {
    representative: {
      name: 'Representative Sampling',
      description: 'Balanced coverage across impression ranges',
    },
    highVolume: {
      name: 'High Volume Priority',
      description: 'Focus on high-traffic keywords',
    },
    stratified: {
      name: 'Stratified Sampling',
      description: 'Proportional representation from each stratum',
    },
    random: {
      name: 'Random Sampling',
      description: 'Simple random selection',
    },
    custom: {
      name: 'Custom Strategy',
      description: 'User-defined sampling logic',
    },
  };

  const calculateMetrics = (strategy: string, sampleSize: number): ComparisonMetric => {
    // Simulate different metrics for each strategy
    const baseMetrics = {
      representative: {
        coverageScore: 85,
        processingTime: 2.5,
        distribution: { high: 20, medium: 50, low: 30 },
        pros: [
          'Balanced keyword representation',
          'Good coverage of all impression ranges',
          'Predictable results',
        ],
        cons: [
          'May miss some high-value outliers',
          'Requires more complex query',
        ],
      },
      highVolume: {
        coverageScore: 65,
        processingTime: 1.8,
        distribution: { high: 70, medium: 25, low: 5 },
        pros: [
          'Focuses on high-impact keywords',
          'Maximizes potential traffic coverage',
          'Simple and fast query',
        ],
        cons: [
          'Misses long-tail keywords',
          'Poor coverage of niche topics',
        ],
      },
      stratified: {
        coverageScore: 90,
        processingTime: 3.2,
        distribution: { high: 33, medium: 34, low: 33 },
        pros: [
          'Excellent overall coverage',
          'Guaranteed representation from each segment',
          'Statistically robust',
        ],
        cons: [
          'More complex implementation',
          'Slower query execution',
        ],
      },
      random: {
        coverageScore: 70,
        processingTime: 1.2,
        distribution: { high: 15, medium: 35, low: 50 },
        pros: [
          'Simple implementation',
          'Fast execution',
          'Unbiased selection',
        ],
        cons: [
          'Unpredictable coverage',
          'May miss important segments',
        ],
      },
      custom: {
        coverageScore: 75,
        processingTime: 2.8,
        distribution: { high: 40, medium: 40, low: 20 },
        pros: [
          'Tailored to specific needs',
          'Flexible parameters',
        ],
        cons: [
          'Requires expertise to design',
          'May need iterations to optimize',
        ],
      },
    };

    const base = baseMetrics[strategy as keyof typeof baseMetrics];
    const costEstimate = sampleSize * apiCostPerQuery;

    return {
      strategy: strategies[strategy as keyof typeof strategies].name,
      sampleSize,
      coverageScore: base.coverageScore,
      costEstimate,
      processingTime: base.processingTime,
      impressionDistribution: base.distribution,
      pros: base.pros,
      cons: base.cons,
    };
  };

  const runComparison = () => {
    setLoading(true);
    
    setTimeout(() => {
      const results = selectedStrategies.map(strategy => 
        calculateMetrics(strategy, sampleSizes[strategy as keyof typeof sampleSizes])
      );
      setComparisonData(results);
      setLoading(false);
    }, 1500);
  };

  const handleStrategyToggle = (strategy: string) => {
    setSelectedStrategies(prev =>
      prev.includes(strategy)
        ? prev.filter(s => s !== strategy)
        : [...prev, strategy]
    );
  };

  const handleSampleSizeChange = (strategy: string, size: number) => {
    setSampleSizes(prev => ({ ...prev, [strategy]: size }));
  };

  // Simple bar chart component using Material-UI
  const SimpleBarChart = ({ data, title }: { data: ComparisonMetric[], title: string }) => {
    const maxCoverage = Math.max(...data.map(d => d.coverageScore));
    
    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>{title}</Typography>
        {data.map((metric, index) => (
          <Box key={index} sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              {metric.strategy}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ flexGrow: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={(metric.coverageScore / maxCoverage) * 100}
                  sx={{ height: 10, borderRadius: 5 }}
                />
              </Box>
              <Typography variant="caption" sx={{ minWidth: 40 }}>
                {metric.coverageScore}%
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Sampling Strategy Comparison Tool
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Compare different sampling strategies side-by-side to find the optimal approach for your SERP analysis.
      </Typography>

      {/* Configuration */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Configuration
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Client ID"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Total Keywords"
                value={totalKeywords}
                onChange={(e) => setTotalKeywords(Number(e.target.value))}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Cost per Query ($)"
                value={apiCostPerQuery}
                onChange={(e) => setApiCostPerQuery(Number(e.target.value))}
                inputProps={{ step: 0.001 }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<CompareIcon />}
                onClick={runComparison}
                disabled={loading || selectedStrategies.length === 0}
                sx={{ height: '56px' }}
              >
                Compare Strategies
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Strategy Selection */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Select Strategies to Compare
          </Typography>
          <Grid container spacing={3}>
            {Object.entries(strategies).map(([key, strategy]) => (
              <Grid item xs={12} md={6} key={key}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedStrategies.includes(key)}
                        onChange={() => handleStrategyToggle(key)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="subtitle1">{strategy.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {strategy.description}
                        </Typography>
                      </Box>
                    }
                  />
                  {selectedStrategies.includes(key) && (
                    <TextField
                      fullWidth
                      type="number"
                      label="Sample Size"
                      value={sampleSizes[key as keyof typeof sampleSizes]}
                      onChange={(e) => handleSampleSizeChange(key, Number(e.target.value))}
                      size="small"
                      sx={{ mt: 2 }}
                      inputProps={{ min: 10, max: 1000 }}
                    />
                  )}
                </Paper>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Comparison Results */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : comparisonData.length > 0 && (
        <>
          {/* Metrics Overview */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {comparisonData.map((metric, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {metric.strategy}
                    </Typography>
                    
                    <Stack spacing={2}>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <CoverageIcon sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="subtitle2">Coverage Score</Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={metric.coverageScore} 
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {metric.coverageScore}% coverage
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CostIcon sx={{ mr: 0.5, fontSize: 16 }} />
                            <Typography variant="caption">Cost</Typography>
                          </Box>
                          <Typography variant="subtitle1">
                            ${metric.costEstimate.toFixed(2)}
                          </Typography>
                        </Box>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <SpeedIcon sx={{ mr: 0.5, fontSize: 16 }} />
                            <Typography variant="caption">Time</Typography>
                          </Box>
                          <Typography variant="subtitle1">
                            {metric.processingTime.toFixed(1)}s
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption">Sample</Typography>
                          <Typography variant="subtitle1">
                            {metric.sampleSize}
                          </Typography>
                        </Box>
                      </Box>

                      <Divider />

                      <Box>
                        <Typography variant="caption" color="success.main">
                          Pros:
                        </Typography>
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                          {metric.pros.map((pro, i) => (
                            <li key={i}>
                              <Typography variant="caption">{pro}</Typography>
                            </li>
                          ))}
                        </ul>
                      </Box>

                      <Box>
                        <Typography variant="caption" color="error.main">
                          Cons:
                        </Typography>
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                          {metric.cons.map((con, i) => (
                            <li key={i}>
                              <Typography variant="caption">{con}</Typography>
                            </li>
                          ))}
                        </ul>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Visual Comparisons */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Coverage Comparison
                  </Typography>
                  <SimpleBarChart data={comparisonData} title="Coverage Score by Strategy" />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Impression Distribution
                  </Typography>
                  {comparisonData.map((metric, index) => (
                    <Box key={index} sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {metric.strategy}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <Box sx={{ flex: metric.impressionDistribution.high, bgcolor: COLORS.high, height: 20, borderRadius: 1 }} />
                        <Box sx={{ flex: metric.impressionDistribution.medium, bgcolor: COLORS.medium, height: 20, borderRadius: 1 }} />
                        <Box sx={{ flex: metric.impressionDistribution.low, bgcolor: COLORS.low, height: 20, borderRadius: 1 }} />
                      </Box>
                      <Stack direction="row" spacing={1}>
                        <Chip size="small" label={`High: ${metric.impressionDistribution.high}%`} sx={{ bgcolor: COLORS.high, color: 'white' }} />
                        <Chip size="small" label={`Med: ${metric.impressionDistribution.medium}%`} sx={{ bgcolor: COLORS.medium, color: 'white' }} />
                        <Chip size="small" label={`Low: ${metric.impressionDistribution.low}%`} sx={{ bgcolor: COLORS.low, color: 'white' }} />
                      </Stack>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Summary Table */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Detailed Comparison
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Strategy</TableCell>
                      <TableCell align="center">Sample Size</TableCell>
                      <TableCell align="center">Total Cost</TableCell>
                      <TableCell align="center">Coverage</TableCell>
                      <TableCell align="center">Processing Time</TableCell>
                      <TableCell align="center">Cost per % Coverage</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {comparisonData.map((metric, index) => (
                      <TableRow key={index}>
                        <TableCell>{metric.strategy}</TableCell>
                        <TableCell align="center">{metric.sampleSize}</TableCell>
                        <TableCell align="center">${metric.costEstimate.toFixed(2)}</TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={`${metric.coverageScore}%`}
                            size="small"
                            color={metric.coverageScore >= 85 ? 'success' : metric.coverageScore >= 70 ? 'warning' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="center">{metric.processingTime.toFixed(1)}s</TableCell>
                        <TableCell align="center">
                          ${(metric.costEstimate / metric.coverageScore).toFixed(4)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default SamplingComparisonDemo; 